import { useEffect, useState } from "react";
import { Check, Crown, ImagePlus, Save, Swords, Users } from "lucide-react";
import { useParams } from "react-router-dom";

import { api, apiError } from "@/lib/api";

const blankEntries = () => Array.from({ length: 10 }, (_, index) => ({ rank: index + 1, fighter_name: "", club: "", points: 0 }));
const emptyRanking = {
  gender: "male",
  weight_class: "",
  division_type: "amateur",
  entries: blankEntries(),
  champion_photo: "",
  champion_name: "",
  champion_club: "",
  titles: [],
  photos: [],
};

function formatKg(value) {
  if (value === null || value === undefined) return "";
  const rounded = Math.round(value * 100) / 100;
  return rounded.toString().replace(".", ",");
}

const KJ_AGE_RANGE_LABEL = {
  U8: "U8 (4–7 Jahre)",
  U10: "U10 (8–9 Jahre)",
  U12: "U12 (10–11 Jahre)",
  U14: "U14 (12–13 Jahre)",
  U16: "U16 (14–15 Jahre)",
  U18: "U18 (16–17 Jahre)",
};

function weightRangeLabel(item, allClasses) {
  if (item.max_weight === null || item.max_weight === undefined) return `über ${formatKg(item.min_weight)} kg`;
  if (!item.min_weight) return `bis ${formatKg(item.max_weight)} kg`;
  const hasLowerNeighbour = allClasses.some((other) => other.gender === item.gender && other.division_type === item.division_type && other.max_weight === item.min_weight && other.id !== item.id);
  const lower = hasLowerNeighbour ? formatKg(item.min_weight + 0.01) : formatKg(item.min_weight);
  return `${lower} – ${formatKg(item.max_weight)} kg`;
}

export default function ManagementPage() {
  const { section } = useParams();
  const [data, setData] = useState([]);
  const [weightClasses, setWeightClasses] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [error, setError] = useState("");
  const [ranking, setRanking] = useState(emptyRanking);
  const [filterDivision, setFilterDivision] = useState("all");
  const [filterAgeClass, setFilterAgeClass] = useState("all");
  const [filterGender, setFilterGender] = useState("all");

  const label = section === "rankings" ? "RANGLISTE PFLEGEN" : section === "users" ? "BENUTZER & ROLLEN" : section === "weight-classes" ? "GEWICHTSKLASSEN" : "ALTERSKLASSEN";

  useEffect(() => {
    setError("");
    if (section === "rankings") {
      Promise.all([api.get("/settings/weight-classes"), api.get("/rankings")])
        .then(([classesResponse, rankingResponse]) => {
          setWeightClasses(classesResponse.data);
          setRankings(rankingResponse.data);
        })
        .catch((requestError) => setError(apiError(requestError)));
      return;
    }
    const endpoint = section === "users" ? "/users" : `/settings/${section}`;
    api.get(endpoint).then((response) => setData(response.data)).catch((requestError) => setError(apiError(requestError)));
  }, [section]);

  const selectWeightClass = (name) => {
    const existing = rankings.find((record) => record.gender === ranking.gender && record.weight_class === name && (record.division_type || "pro") === ranking.division_type);
    if (existing) {
      const entries = blankEntries().map((row) => {
        const persisted = existing.entries?.find((entry) => entry.rank === row.rank);
        return persisted ? { ...row, ...persisted } : row;
      });
      setRanking({
        gender: ranking.gender,
        weight_class: name,
        division_type: ranking.division_type,
        entries,
        champion_photo: existing.champion_photo || "",
        champion_name: existing.champion_name || "",
        champion_club: existing.champion_club || "",
        titles: existing.titles || [],
        photos: existing.photos || [],
      });
    } else {
      setRanking({ ...emptyRanking, gender: ranking.gender, division_type: ranking.division_type, weight_class: name });
    }
  };

  const changeGender = (nextGender) => {
    setRanking({ ...emptyRanking, gender: nextGender, division_type: ranking.division_type });
  };

  const changeDivision = (nextDivision) => {
    setRanking({ ...emptyRanking, gender: ranking.gender, division_type: nextDivision });
  };

  const saveRanking = async (event) => {
    event.preventDefault();
    try {
      await api.put("/rankings", ranking);
      const refreshed = await api.get("/rankings");
      setRankings(refreshed.data);
      setError("Rangliste wurde gespeichert.");
    } catch (requestError) {
      setError(apiError(requestError));
    }
  };

  const changeRole = async (user, role) => {
    try {
      await api.put(`/users/${user.id}/role`, { role });
      setData(data.map((item) => item.id === user.id ? { ...item, role } : item));
    } catch (requestError) {
      setError(apiError(requestError));
    }
  };

  if (section === "rankings") return (
    <div className="content-frame" data-testid="ranking-management-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">ADMIN-BEREICH</p>
          <h1>{label}</h1>
          <p className="muted">Champion wird als Foto oben angezeigt. Ranglistenplatz #1 ist der erste Contender / Herausforderer.</p>
        </div>
      </header>
      {error && <div className="form-error" data-testid="ranking-management-message">{error}</div>}
      <form className="ranking-editor" onSubmit={saveRanking}>
        <div className="division-toggle" role="tablist" aria-label="Turnier-Typ" data-testid="ranking-editor-division-toggle">
          <button type="button" role="tab" aria-selected={ranking.division_type === "amateur"} className={`division-tab ${ranking.division_type === "amateur" ? "active" : ""}`} onClick={() => changeDivision("amateur")} data-testid="ranking-editor-division-amateur">AMATEUR</button>
          <button type="button" role="tab" aria-selected={ranking.division_type === "pro"} className={`division-tab ${ranking.division_type === "pro" ? "active" : ""}`} onClick={() => changeDivision("pro")} data-testid="ranking-editor-division-pro">PROFI</button>
        </div>
        <div className="form-grid">
          <label>Geschlecht
            <select value={ranking.gender} onChange={(event) => changeGender(event.target.value)} data-testid="ranking-gender-select">
              <option value="male">Herren</option>
              <option value="female">Damen</option>
            </select>
          </label>
          <label>Gewichtsklasse (kg)
            <select required value={ranking.weight_class} onChange={(event) => selectWeightClass(event.target.value)} data-testid="ranking-weight-class-select">
              <option value="">Kilogramm wählen</option>
              {weightClasses.filter((item) => item.gender === ranking.gender && item.division_type === ranking.division_type).map((item) => (
                <option key={item.id} value={item.name}>{item.name}</option>
              ))}
            </select>
          </label>
        </div>
        <fieldset className="ranking-champion-block">
          <legend><Crown size={16} /> CHAMPION</legend>
          <div className="form-grid">
            <label>Champion-Name<input value={ranking.champion_name} onChange={(event) => setRanking({ ...ranking, champion_name: event.target.value })} placeholder="Vor- und Nachname" data-testid="ranking-champion-name-input" /></label>
            <label>Verein<input value={ranking.champion_club} onChange={(event) => setRanking({ ...ranking, champion_club: event.target.value })} placeholder="Vereinsname" data-testid="ranking-champion-club-input" /></label>
          </div>
          <label>Champion-Foto
            <div className="calendar-image-picker">
              <input type="file" accept="image/*" onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => setRanking({ ...ranking, champion_photo: reader.result });
                reader.readAsDataURL(file);
              }} data-testid="ranking-champion-photo-input" />
              {!ranking.champion_photo && <span><ImagePlus size={16} /> Foto vom Handy oder PC hochladen</span>}
            </div>
          </label>
          {ranking.champion_photo && <div className="fighter-photo-preview"><img src={ranking.champion_photo} alt="Vorschau Champion" data-testid="ranking-champion-photo-preview" /><button type="button" className="button ghost" onClick={() => setRanking({ ...ranking, champion_photo: "" })} data-testid="ranking-champion-photo-remove">Foto entfernen</button></div>}
          <div style={{marginTop:16,paddingTop:12,borderTop:"1px solid rgba(255,255,255,.1)"}}>
            <b style={{fontSize:12,letterSpacing:0.6,color:"#f4d33e"}}>TITEL</b>
            <p className="muted" style={{fontSize:11,margin:"4px 0 8px"}}>Mehrere Titel möglich. Datum ist optional.</p>
            {(ranking.titles || []).map((t, idx) => <div key={idx} style={{display:"flex",gap:6,marginBottom:6,flexWrap:"wrap"}}>
              <input placeholder="Titel-Bezeichnung" value={t.label} onChange={(e) => { const arr=[...ranking.titles]; arr[idx]={...t,label:e.target.value}; setRanking({...ranking,titles:arr}); }} data-testid={`ranking-title-label-${idx}`} style={{flex:2,minWidth:180}} />
              <input placeholder="Datum (optional)" value={t.date||""} onChange={(e) => { const arr=[...ranking.titles]; arr[idx]={...t,date:e.target.value}; setRanking({...ranking,titles:arr}); }} data-testid={`ranking-title-date-${idx}`} style={{flex:1,minWidth:100}} />
              <button type="button" className="mini-btn" onClick={() => { const arr=[...ranking.titles]; if(idx>0){[arr[idx-1],arr[idx]]=[arr[idx],arr[idx-1]];} setRanking({...ranking,titles:arr}); }} data-testid={`ranking-title-up-${idx}`}>↑</button>
              <button type="button" className="mini-btn" onClick={() => { const arr=[...ranking.titles]; if(idx<arr.length-1){[arr[idx+1],arr[idx]]=[arr[idx],arr[idx+1]];} setRanking({...ranking,titles:arr}); }} data-testid={`ranking-title-down-${idx}`}>↓</button>
              <button type="button" className="mini-btn" style={{background:"#8a2a2a",color:"#fff",border:"none"}} onClick={() => { const arr=ranking.titles.filter((_,i)=>i!==idx); setRanking({...ranking,titles:arr}); }} data-testid={`ranking-title-remove-${idx}`}>✕</button>
            </div>)}
            <button type="button" className="button ghost" onClick={() => setRanking({...ranking,titles:[...(ranking.titles||[]),{label:"",date:""}]})} data-testid="ranking-title-add" style={{fontSize:12}}>+ Titel hinzufügen</button>
          </div>
          <div style={{marginTop:16,paddingTop:12,borderTop:"1px solid rgba(255,255,255,.1)"}}>
            <b style={{fontSize:12,letterSpacing:0.6,color:"#f4d33e"}}>ZUSÄTZLICHE FOTOS (SLIDER)</b>
            <p className="muted" style={{fontSize:11,margin:"4px 0 8px"}}>Werden auf der Champion-Seite als Slider gezeigt. Ein Foto als Haupt markieren.</p>
            <input type="file" accept="image/*" onChange={(e) => {
              const file = e.target.files?.[0]; if (!file) return;
              const r = new FileReader();
              r.onload = () => { setRanking({...ranking,photos:[...(ranking.photos||[]),{data_base64:r.result,main:!(ranking.photos||[]).some((p)=>p.main)}]}); e.target.value=""; };
              r.readAsDataURL(file);
            }} data-testid="ranking-photo-add" />
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>{(ranking.photos||[]).map((p,idx) => <div key={idx} style={{position:"relative",width:96,textAlign:"center"}}>
              <img src={p.data_base64} alt="" style={{width:96,height:96,objectFit:"cover",borderRadius:6,border:p.main?"3px solid #f4d33e":"1px solid rgba(255,255,255,.15)"}} data-testid={`ranking-photo-${idx}`} />
              <div style={{display:"flex",gap:3,marginTop:3,justifyContent:"center"}}>
                <button type="button" className="mini-btn" title="Als Haupt markieren" onClick={() => { const arr=(ranking.photos||[]).map((x,i)=>({...x,main:i===idx})); setRanking({...ranking,photos:arr}); }} data-testid={`ranking-photo-main-${idx}`}>★</button>
                <button type="button" className="mini-btn" title="Nach vorn" onClick={() => { const arr=[...ranking.photos]; if(idx>0){[arr[idx-1],arr[idx]]=[arr[idx],arr[idx-1]];} setRanking({...ranking,photos:arr}); }} data-testid={`ranking-photo-up-${idx}`}>←</button>
                <button type="button" className="mini-btn" title="Nach hinten" onClick={() => { const arr=[...ranking.photos]; if(idx<arr.length-1){[arr[idx+1],arr[idx]]=[arr[idx],arr[idx+1]];} setRanking({...ranking,photos:arr}); }} data-testid={`ranking-photo-down-${idx}`}>→</button>
                <button type="button" className="mini-btn" style={{background:"#8a2a2a",color:"#fff",border:"none"}} onClick={() => setRanking({...ranking,photos:ranking.photos.filter((_,i)=>i!==idx)})} data-testid={`ranking-photo-remove-${idx}`}>✕</button>
              </div>
            </div>)}</div>
          </div>
        </fieldset>
        <fieldset className="ranking-contender-block">
          <legend><Swords size={16} /> CONTENDER · Platz #1 = erster Herausforderer</legend>
          {ranking.entries.map((entry, index) => (
            <div className="ranking-edit-row" key={entry.rank}>
              <b>#{entry.rank}</b>
              <input placeholder="Kämpfername" value={entry.fighter_name} onChange={(event) => { const entries = [...ranking.entries]; entries[index] = { ...entry, fighter_name: event.target.value }; setRanking({ ...ranking, entries }); }} data-testid={`ranking-name-${entry.rank}-input`} />
              <input placeholder="Verein" value={entry.club} onChange={(event) => { const entries = [...ranking.entries]; entries[index] = { ...entry, club: event.target.value }; setRanking({ ...ranking, entries }); }} data-testid={`ranking-club-${entry.rank}-input`} />
              <input type="number" placeholder="Punkte" value={entry.points} onChange={(event) => { const entries = [...ranking.entries]; entries[index] = { ...entry, points: Number(event.target.value) }; setRanking({ ...ranking, entries }); }} data-testid={`ranking-points-${entry.rank}-input`} />
            </div>
          ))}
        </fieldset>
        <button className="button primary" data-testid="save-ranking-button"><Save size={18} /> RANGLISTE SPEICHERN</button>
      </form>
    </div>
  );

  return (
    <div className="content-frame" data-testid="management-page">
      <header className="page-heading"><div><p className="eyebrow">ADMIN-BEREICH</p><h1>{label}</h1></div></header>
      {error && <div className="form-error" data-testid="management-error">{error}</div>}
      {section === "weight-classes" && <section className="filter-row" style={{display:"flex",gap:12,flexWrap:"wrap",padding:"8px 0 16px",alignItems:"center"}} data-testid="weight-class-filters">
        <label style={{display:"flex",flexDirection:"column",fontSize:11,gap:4}}><span style={{color:"#8a8a8a",letterSpacing:0.6}}>TURNIER-TYP</span><select value={filterDivision} onChange={(e) => { setFilterDivision(e.target.value); setFilterAgeClass("all"); }} data-testid="filter-division"><option value="all">Alle</option><option value="amateur">Adult Amateur</option><option value="pro">Adult Profi</option><option value="kinder_jugend">Kinder & Jugend</option></select></label>
        {filterDivision === "kinder_jugend" && <label style={{display:"flex",flexDirection:"column",fontSize:11,gap:4}}><span style={{color:"#8a8a8a",letterSpacing:0.6}}>ALTERSKLASSE</span><select value={filterAgeClass} onChange={(e) => setFilterAgeClass(e.target.value)} data-testid="filter-age-class"><option value="all">Alle</option><option value="U8">U8 (4–7 Jahre)</option><option value="U10">U10 (8–9 Jahre)</option><option value="U12">U12 (10–11 Jahre)</option><option value="U14">U14 (12–13 Jahre)</option><option value="U16">U16 (14–15 Jahre)</option><option value="U18">U18 (16–17 Jahre)</option></select></label>}
        <label style={{display:"flex",flexDirection:"column",fontSize:11,gap:4}}><span style={{color:"#8a8a8a",letterSpacing:0.6}}>GESCHLECHT</span><select value={filterGender} onChange={(e) => setFilterGender(e.target.value)} data-testid="filter-gender"><option value="all">Alle</option><option value="male">männlich</option><option value="female">weiblich</option></select></label>
      </section>}
      <section className="management-list">
        {(section === "weight-classes" ? data.filter((it) => (filterDivision === "all" || it.division_type === filterDivision) && (filterDivision !== "kinder_jugend" || filterAgeClass === "all" || it.age_class === filterAgeClass) && (filterGender === "all" || it.gender === filterGender)).slice().sort((a, b) => (a.min_weight ?? 0) - (b.min_weight ?? 0)) : data).map((item) => (
          <article key={item.id} data-testid={`management-row-${item.id}`}>
            <div>{section === "users" ? <Users /> : <Check />}<span><b>{section === "users" ? item.full_name : item.name}</b><small>{section === "users" ? item.email : section === "weight-classes" ? `${item.gender === "male" ? "männlich" : "weiblich"} · ${weightRangeLabel(item, data)}${item.division_type ? " · " + (item.division_type === "amateur" ? "Adult Amateur" : item.division_type === "pro" ? "Adult Profi" : "Kinder & Jugend") : ""}${item.age_class ? " · " + (KJ_AGE_RANGE_LABEL[item.age_class] || item.age_class) : ""}` : `${item.min_age} – ${item.max_age || "∞"} Jahre`}</small></span></div>
            {section === "users" && (
              <select value={item.role} onChange={(event) => changeRole(item, event.target.value)} data-testid={`user-role-select-${item.id}`}>
                {["super_admin", "event_admin", "trainer", "fighter", "spectator"].map((role) => <option key={role} value={role}>{role}</option>)}
              </select>
            )}
          </article>
        ))}
      </section>
    </div>
  );
}
